import { useCallback, useEffect, useRef, useState } from "react";

const useScrollSpy = (sectionIds, { clickLockMs = 800, ready = true } = {}) => {
  const [activeId, setActiveId] = useState(sectionIds[0]);
  const clickLockUntil = useRef(0);
  const ratios = useRef({});

  useEffect(() => {
    if (!ready) return undefined;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          ratios.current[entry.target.id] = entry.isIntersecting
            ? entry.intersectionRatio
            : 0;
        });
        if (Date.now() < clickLockUntil.current) return;
        let best = null;
        sectionIds.forEach((id) => {
          if ((ratios.current[id] || 0) > (best ? ratios.current[best] : 0)) {
            best = id;
          }
        });
        if (best) setActiveId(best);
      },
      { root: null, rootMargin: "-45% 0px -50% 0px", threshold: [0, 0.25, 0.5, 1] }
    );
    sectionIds.forEach((id) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sectionIds.join(","), ready]);

  const scrollToSection = useCallback(
    (id) => {
      clickLockUntil.current = Date.now() + clickLockMs;
      setActiveId(id);
      const el = document.getElementById(id);
      if (!el) return;

      let container = el.parentElement;
      while (container && container !== document.body) {
        const style = window.getComputedStyle(container);
        if (
          /(auto|scroll)/.test(style.overflowY) &&
          container.scrollHeight > container.clientHeight
        ) {
          break;
        }
        container = container.parentElement;
      }

      if (container && container !== document.body) {
        const offset =
          el.getBoundingClientRect().top -
          container.getBoundingClientRect().top +
          container.scrollTop -
          80; // clears the sticky nav bar
        container.scrollTo({ top: offset, behavior: "smooth" });
      } else {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    },
    [clickLockMs]
  );

  return { activeId, scrollToSection };
};

export default useScrollSpy;
